import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { computeChecklistProgress, isScheduleItemReadyToComplete, buildCompletionBlockMessage, type LinkedChecklistItem } from '@/lib/scheduleChecklistProgress';

export interface ScheduleChecklistProgress {
  totalLinked: number;
  completedCount: number;
  managementCheckedCount: number;
  subcontractorCheckedCount: number;
  requiredCount: number;
  completedRequiredCount: number;
  progressPct: number;
  allRequiredComplete: boolean;
  hasLinkedItems: boolean;
  missingManagement: number;
  missingSubcontractor: number;
  blockedMessage: string | null;
  readyToComplete: boolean;
  totalRequiredUnits: number;
  totalCompletedUnits: number;
}

export function useScheduleChecklistProgress(projectId: string) {
  const [linkedChecklists, setLinkedChecklists] = useState<Map<string, LinkedChecklistItem[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedOnceRef = useRef(false);

  const load = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    if (!hasLoadedOnceRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      // Step 1: Fetch all schedule item IDs for this project (exclude soft-deleted)
      const { data: scheduleItems } = await supabase
        .from('jphph_project_schedule_items')
        .select('id')
        .eq('project_id', projectId)
        .eq('is_deleted', false);

      const scheduleIds = (scheduleItems || []).map((s: { id: string }) => s.id);

      // Step 2: Fetch all checklist items for this project
      const [{ data: ci }, { data: checklists }] = await Promise.all([
        supabase
          .from('jphph_checklist_items')
          .select('id, title, is_completed, management_checked, management_checked_at, management_checked_by, subcontractor_checked, subcontractor_checked_at, subcontractor_checked_by, category, checklist_id, requires_management_check, requires_subcontractor_check')
          .eq('project_id', projectId)
          .order('sort_order', { ascending: true }),
        supabase
          .from('jphph_checklists')
          .select('id, title')
          .eq('project_id', projectId),
      ]);

      const clMap = new Map((checklists || []).map((c: { id: string; title: string }) => [c.id, c.title]));
      const ciMap = new Map(
        (ci || []).map((c: {
          id: string; title: string; is_completed: boolean;
          management_checked: boolean; management_checked_at: string | null; management_checked_by: string | null;
          subcontractor_checked: boolean; subcontractor_checked_at: string | null; subcontractor_checked_by: string | null;
          category: string; checklist_id: string;
          requires_management_check: boolean; requires_subcontractor_check: boolean;
        }) => [c.id, {
          id: c.id,
          title: c.title,
          is_completed: c.is_completed || (c.management_checked && c.subcontractor_checked),
          management_checked: c.management_checked,
          subcontractor_checked: c.subcontractor_checked,
          category: c.category,
          checklistTitle: clMap.get(c.checklist_id) || null,
          confidence: null,
          mappingType: null,
          mappingId: null,
          requires_management_check: c.requires_management_check,
          requires_subcontractor_check: c.requires_subcontractor_check,
        }])
      );

      // Step 3: Fetch mappings — batched to avoid URL length limits
      async function fetchMappingsBatch(column: string, ids: string[], batchSize = 50): Promise<Array<{
        id: string;
        project_schedule_item_id: string | null;
        project_checklist_item_id: string | null;
        checklist_template_item_id: string | null;
        schedule_template_item_id: string | null;
        mapping_type: string | null;
        confidence: string | null;
      }>> {
        const all: Array<{
          id: string;
          project_schedule_item_id: string | null;
          project_checklist_item_id: string | null;
          checklist_template_item_id: string | null;
          schedule_template_item_id: string | null;
          mapping_type: string | null;
          confidence: string | null;
        }> = [];
        for (let i = 0; i < ids.length; i += batchSize) {
          const batch = ids.slice(i, i + batchSize);
          const { data, error } = await supabase
            .from('jphph_checklist_schedule_item_mappings')
            .select('id, project_schedule_item_id, project_checklist_item_id, checklist_template_item_id, schedule_template_item_id, mapping_type, confidence')
            .in(column, batch);
          if (error) {
            // eslint-disable-next-line no-console
            console.error(`ScheduleChecklistProgress mapping batch query failed (${column} batch ${i}):`, error.message);
            continue;
          }
          all.push(...(data as typeof all));
        }
        return all;
      }

      let mappings: Array<{
        id: string;
        project_schedule_item_id: string | null;
        project_checklist_item_id: string | null;
        checklist_template_item_id: string | null;
        schedule_template_item_id: string | null;
        mapping_type: string | null;
        confidence: string | null;
      }> = [];

      if (scheduleIds.length > 0) {
        const pm = await fetchMappingsBatch('project_schedule_item_id', scheduleIds);
        mappings = [...pm];
      }

      // Also fetch by template item IDs (fallback linkage)
      const { data: scheduleItemsWithTemplate } = await supabase
        .from('jphph_project_schedule_items')
        .select('id, source_template_item_id')
        .eq('project_id', projectId)
        .eq('is_deleted', false)
        .not('source_template_item_id', 'is', null);

      const templateIdToScheduleId = new Map<string, string>();
      for (const s of (scheduleItemsWithTemplate || [])) {
        if (s.source_template_item_id) {
          templateIdToScheduleId.set(s.source_template_item_id, s.id);
        }
      }

      const templateIds = Array.from(templateIdToScheduleId.keys());
      if (templateIds.length > 0) {
        const tm = await fetchMappingsBatch('schedule_template_item_id', templateIds);
        for (const row of tm) {
          if (!mappings.some((m) => m.id === row.id)) {
            mappings.push({
              ...row,
              project_schedule_item_id: row.project_schedule_item_id || templateIdToScheduleId.get(row.schedule_template_item_id!) || null,
            });
          }
        }
      }

      // Also fetch mappings by checklist item IDs
      const checklistIds = Array.from(ciMap.keys());
      if (checklistIds.length > 0) {
        const cm = await fetchMappingsBatch('project_checklist_item_id', checklistIds);
        for (const row of cm) {
          if (!mappings.some((m) => m.id === row.id)) {
            // If this mapping has a project_schedule_item_id that belongs to our project, include it
            if (row.project_schedule_item_id && scheduleIds.includes(row.project_schedule_item_id)) {
              mappings.push(row);
            }
          }
        }
      }

      const result = new Map<string, LinkedChecklistItem[]>();

      for (const row of mappings) {
        const scheduleItemId = row.project_schedule_item_id;
        const checklistItemId = row.project_checklist_item_id;
        if (!scheduleItemId || !checklistItemId) continue;

        const c = ciMap.get(checklistItemId);
        if (!c) continue;

        const arr = result.get(scheduleItemId) || [];
        arr.push({
          ...c,
          confidence: row.confidence,
          mappingType: row.mapping_type,
          mappingId: row.id,
        });
        result.set(scheduleItemId, arr);
      }

      setLinkedChecklists(result);
      hasLoadedOnceRef.current = true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load checklist progress');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  // Realtime subscription: auto-reload when checklist items change
  useEffect(() => {
    if (!projectId) return;

    const channel = supabase
      .channel(`checklist-items-realtime-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'jphph_checklist_items',
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          load();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, load]);

  const getProgress = useCallback(
    (scheduleItemId: string): ScheduleChecklistProgress => {
      const items = linkedChecklists.get(scheduleItemId) || [];
      const progress = computeChecklistProgress(items);
      return {
        ...progress,
        blockedMessage: buildCompletionBlockMessage(items),
        readyToComplete: isScheduleItemReadyToComplete(items),
      };
    },
    [linkedChecklists]
  );

  const progressMap = useMemo(() => {
    const map = new Map<string, ScheduleChecklistProgress>();
    for (const [id, items] of linkedChecklists) {
      const progress = computeChecklistProgress(items);
      map.set(id, {
        ...progress,
        blockedMessage: buildCompletionBlockMessage(items),
        readyToComplete: isScheduleItemReadyToComplete(items),
      });
    }
    return map;
  }, [linkedChecklists]);

  const reload = useCallback(() => {
    load();
  }, [load]);

  return {
    linkedChecklists,
    progressMap,
    getProgress,
    loading,
    error,
    reload,
  };
}